import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  ConnectionMode,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCollide,
  forceX,
  forceY,
  SimulationNodeDatum,
  SimulationLinkDatum,
} from "d3-force";
import { Loader2 } from "lucide-react";
import { useADStore } from "../../store/adStore";
import { ADNode, ADNodeData } from "./ADNode";
import { ADEdge } from "./ADEdge";

/**
 * ADAnalyticsGraph — ReactFlow + d3-force renderer.
 *
 * Pattern modeled after the CloudGraph renderer (very robust on big graphs):
 *   1. Convert store data → React Flow nodes/edges.
 *   2. On a fresh dataset, scatter nodes around the canvas perimeter so the
 *      sim has somewhere to "explode from", then a high-energy run pushes
 *      them apart.
 *   3. Live tick-based position writes — every d3 tick we patch the React
 *      Flow nodes so the user sees the layout assemble in real time.
 *   4. Drag handling pins fx/fy during drag, reheats on release.
 *
 * Cluster behavior is emergent: strong charge + collision + weak link
 * attraction naturally clumps connected nodes together while pushing
 * unrelated subgraphs apart. No explicit community detection needed.
 */

// ── d3-force types ───────────────────────────────────────────────────
interface D3Node extends SimulationNodeDatum {
  id: string;
  isHub: boolean;
  /** True when this node only has one connection — i.e. it's a leaf.
   *  Leaves are pulled in tighter to their parent via a halved link
   *  distance so they look like petals on a flower instead of long
   *  spokes on a wheel. */
  isLeaf: boolean;
  visualSize: number;
  labelHalfWidth: number;
  fx?: number | null;
  fy?: number | null;
}

interface D3Link extends SimulationLinkDatum<D3Node> {
  source: string | D3Node;
  target: string | D3Node;
}

const nodeTypes = { adNode: ADNode };
const edgeTypes = { adEdge: ADEdge };

interface GraphEdgeLike {
  id: string;
  source: string;
  target: string;
  label?: string;
}

// ── Visual tiers ─────────────────────────────────────────────────────
// Returned values inform both ADNode rendering and the collision force.
function pickVisualTier(graphSize: number) {
  if (graphSize <= 30) {
    return { tier: "huge" as const, size: 240, labelHalfWidth: 220, showEdgeLabels: true, showNodeLabels: true };
  }
  if (graphSize <= 80) {
    return { tier: "large" as const, size: 200, labelHalfWidth: 180, showEdgeLabels: true, showNodeLabels: true };
  }
  if (graphSize <= 200) {
    return { tier: "medium" as const, size: 160, labelHalfWidth: 140, showEdgeLabels: false, showNodeLabels: true };
  }
  if (graphSize <= 500) {
    return { tier: "small" as const, size: 120, labelHalfWidth: 110, showEdgeLabels: false, showNodeLabels: true };
  }
  return { tier: "tiny" as const, size: 80, labelHalfWidth: 80, showEdgeLabels: false, showNodeLabels: false };
}

// ── Edge dedup ───────────────────────────────────────────────────────
// AD queries occasionally return mirrored A↔B edges with the same label.
// We keep one per (a,b,label) regardless of direction.
function dedupeEdges(edges: GraphEdgeLike[]): GraphEdgeLike[] {
  const exact = new Map<string, GraphEdgeLike>();
  for (const e of edges) {
    const exactKey = `${e.source}→${e.target}::${e.label ?? ""}`;
    if (!exact.has(exactKey)) exact.set(exactKey, e);
  }
  const collapsed = new Map<string, GraphEdgeLike>();
  for (const e of exact.values()) {
    const left = e.source < e.target ? e.source : e.target;
    const right = e.source < e.target ? e.target : e.source;
    const key = `${left}↔${right}::${e.label ?? ""}`;
    const existing = collapsed.get(key);
    if (!existing) {
      collapsed.set(key, e);
    } else {
      // deterministic tiebreak so SSR / re-runs are stable
      const a = `${existing.source}|${existing.target}|${existing.id}`;
      const b = `${e.source}|${e.target}|${e.id}`;
      if (b < a) collapsed.set(key, e);
    }
  }
  return [...collapsed.values()];
}

function ADReactFlowGraphInner() {
  const { graphNodes, graphEdges, isLoadingGraph, selectNode } = useADStore();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<ADNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const { fitView } = useReactFlow();

  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<ReturnType<typeof forceSimulation<D3Node>> | null>(null);
  const d3NodesRef = useRef<D3Node[]>([]);
  const positionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const lastDatasetKeyRef = useRef<string>("");
  const graphNodesRef = useRef(graphNodes);
  graphNodesRef.current = graphNodes;

  // ── Memoize the visual tier so children don't churn unnecessarily ─
  const tier = useMemo(() => pickVisualTier(graphNodes.length), [graphNodes.length]);

  const stopSimulation = useCallback(() => {
    if (simulationRef.current) {
      simulationRef.current.stop();
      simulationRef.current = null;
    }
  }, []);

  // ── Master effect: rebuild + run layout whenever the dataset changes ─
  useEffect(() => {
    const containerWidth = containerRef.current?.offsetWidth || 1200;
    const containerHeight = containerRef.current?.offsetHeight || 800;
    const centerX = containerWidth / 2;
    const centerY = containerHeight / 2;

    if (graphNodes.length === 0) {
      stopSimulation();
      setNodes([]);
      setEdges([]);
      positionsRef.current.clear();
      lastDatasetKeyRef.current = "";
      return;
    }

    // Detect dataset changes by stable id ordering. Filter / panel toggles
    // don't change this — only a new query does.
    const datasetKey = graphNodes.map((n) => n.id).sort().join(",");
    const isNewDataset = datasetKey !== lastDatasetKeyRef.current;
    lastDatasetKeyRef.current = datasetKey;

    // ── Filter & dedupe edges ────────────────────────────────
    const validIds = new Set(graphNodes.map((n) => n.id));
    const cleanEdges = dedupeEdges(
      graphEdges.filter((e) => validIds.has(e.source) && validIds.has(e.target)) as GraphEdgeLike[],
    );

    // Degree map → drives "hub" treatment in collision + charge.
    const degree = new Map<string, number>();
    graphNodes.forEach((n) => degree.set(n.id, 0));
    cleanEdges.forEach((e) => {
      degree.set(e.source, (degree.get(e.source) || 0) + 1);
      degree.set(e.target, (degree.get(e.target) || 0) + 1);
    });
    const maxDegree = Math.max(...degree.values(), 1);
    const hubThreshold = Math.max(4, maxDegree * 0.4);

    // ── Build React Flow nodes (positions filled below) ──────
    const nodeMap = new Map(graphNodes.map((n) => [n.id, n]));
    const flowNodes: Node<ADNodeData>[] = graphNodes.map((g, idx) => {
      const cached = positionsRef.current.get(g.id);
      let x: number, y: number;

      if (cached && !isNewDataset) {
        x = cached.x;
        y = cached.y;
      } else {
        // Scatter around the perimeter so the simulation has somewhere
        // to "explode from".  Random ring of radius depending on graph size.
        const radius = Math.max(900, Math.min(2200, 700 + Math.sqrt(graphNodes.length) * 90));
        const angle = (idx / Math.max(1, graphNodes.length)) * Math.PI * 2 + Math.random() * 0.5;
        const jitter = 0.85 + Math.random() * 0.3;
        x = centerX + Math.cos(angle) * radius * jitter;
        y = centerY + Math.sin(angle) * radius * jitter;
      }

      return {
        id: g.id,
        type: "adNode",
        position: { x, y },
        draggable: true,
        selectable: true,
        data: {
          label: g.label,
          kind: g.kind,
          isTierZero: g.isTierZero,
          isOwnedObject: g.isOwnedObject,
          nodeId: g.id,
          graphSize: graphNodes.length,
          sizeTier: tier.tier,
          showLabel: tier.showNodeLabels,
        },
      };
    });

    // ── Build React Flow edges ──────────────────────────────
    const flowEdges: Edge[] = cleanEdges.map((e) => ({
      id: `${e.source}__${e.target}__${e.label ?? ""}`,
      source: e.source,
      target: e.target,
      type: "adEdge",
      label: e.label,
      selectable: false,
      focusable: false,
      data: {
        showLabel: tier.showEdgeLabels,
        graphSize: graphNodes.length,
      },
    }));

    setNodes(flowNodes);
    setEdges(flowEdges);

    // ── Spin up the d3-force simulation ──────────────────────
    stopSimulation();

    // Adaptive collision radius — circle + label allowance + a little padding.
    // High-degree hubs get a touch more breathing room.
    const baseCollide = tier.size / 2 + tier.labelHalfWidth + 40;
    const d3Nodes: D3Node[] = flowNodes.map((rfNode) => {
      const g = nodeMap.get(rfNode.id);
      const deg = degree.get(rfNode.id) || 0;
      return {
        id: rfNode.id,
        x: rfNode.position.x,
        y: rfNode.position.y,
        isHub: !!g && deg >= hubThreshold,
        isLeaf: deg === 1,
        visualSize: tier.size,
        labelHalfWidth: tier.labelHalfWidth,
        fx: null,
        fy: null,
      };
    });
    d3NodesRef.current = d3Nodes;

    const d3Links: D3Link[] = flowEdges.map((e) => ({
      source: e.source,
      target: e.target,
    }));

    const n = graphNodes.length;
    const isHuge = n > 500;
    const isLarge = n > 200;
    const isMedium = n > 80;

    // ── Force tuning (CloudGraph-style: strong, simple, robust) ─
    // Charge is the hammer — pushes everything apart so labels never overlap.
    // It is scaled down for very dense graphs because Barnes-Hut still has
    // to do real work and the collision force does the heavy lifting at
    // close range.
    const chargeStrength = isHuge ? -8000 : isLarge ? -14000 : isMedium ? -20000 : -28000;

    // Link distance is generous — connected nodes still need long lines so
    // their respective neighborhoods don't slam together.
    const linkDistance = isHuge ? 520 : isLarge ? 600 : isMedium ? 680 : 760;

    // Centering is weak so clusters don't collapse onto the origin.
    const centerStrength = isHuge ? 0.012 : isLarge ? 0.014 : 0.018;

    // Collision: scaled by tier + extra room for hubs.
    const collisionRadius = (d: D3Node) => baseCollide + (d.isHub ? 60 : 0);

    // Resolve a link endpoint to its D3Node, even before d3 has converted
    // the string ids to object refs (this happens lazily during init).
    const idToNode = new Map(d3Nodes.map((d) => [d.id, d] as const));
    const resolveEnd = (end: string | D3Node): D3Node | undefined =>
      typeof end === "object" ? end : idToNode.get(end);

    const simulation = forceSimulation<D3Node>(d3Nodes)
      .force(
        "link",
        forceLink<D3Node, D3Link>(d3Links)
          .id((d) => d.id)
          .distance((link) => {
            const s = resolveEnd(link.source);
            const t = resolveEnd(link.target);
            // If either endpoint is a leaf (degree 1), halve the line so
            // the leaf nestles close to its parent.
            const leafEdge = (s?.isLeaf ?? false) || (t?.isLeaf ?? false);
            return leafEdge ? linkDistance * 0.5 : linkDistance;
          })
          .strength((link) => {
            const s = resolveEnd(link.source);
            const t = resolveEnd(link.target);
            // Leaf links pull a touch harder so the leaf actually parks
            // at the shorter ideal distance instead of being yanked away
            // by neighbor repulsion.
            const leafEdge = (s?.isLeaf ?? false) || (t?.isLeaf ?? false);
            return leafEdge ? 0.4 : 0.18;
          }),
      )
      .force(
        "charge",
        forceManyBody<D3Node>()
          .strength(chargeStrength)
          .distanceMax(isHuge ? 2400 : isLarge ? 3200 : 4000)
          .theta(isHuge ? 0.95 : 0.85),
      )
      .force(
        "collide",
        forceCollide<D3Node>()
          .radius(collisionRadius)
          .strength(1)
          .iterations(isHuge ? 2 : 4),
      )
      .force("centerX", forceX<D3Node>(centerX).strength(centerStrength))
      .force("centerY", forceY<D3Node>(centerY).strength(centerStrength))
      .alphaDecay(isHuge ? 0.012 : isLarge ? 0.008 : 0.005)
      .velocityDecay(isHuge ? 0.55 : 0.45)
      .alpha(isNewDataset ? (isHuge ? 0.6 : 0.85) : 0.25)
      .alphaMin(0.01);

    simulationRef.current = simulation;

    // ── Live tick → setNodes ───────────────────────────────
    // d3 mutates d3Nodes in place; on each tick we patch React Flow.
    simulation.on("tick", () => {
      setNodes((current) =>
        current.map((rfNode) => {
          const d = d3Nodes.find((dd) => dd.id === rfNode.id);
          if (!d || !Number.isFinite(d.x) || !Number.isFinite(d.y)) return rfNode;
          const x = d.x as number;
          const y = d.y as number;
          if (Math.abs(rfNode.position.x - x) < 0.2 && Math.abs(rfNode.position.y - y) < 0.2) {
            return rfNode;
          }
          positionsRef.current.set(rfNode.id, { x, y });
          return { ...rfNode, position: { x, y } };
        }),
      );
    });

    // ── Auto fit camera once when the dataset is fresh ─────
    if (isNewDataset) {
      // Two rAFs let React Flow compute internal node bounds first.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          fitView({
            padding: isHuge ? 0.08 : isLarge ? 0.12 : 0.18,
            duration: 600,
            minZoom: 0.005,
            maxZoom: 1.4,
            includeHiddenNodes: true,
          });
        }),
      );
    }

    return () => {
      stopSimulation();
    };
    // graphNodes / graphEdges are the inputs that justify a relayout.
    // tier comes from graphNodes.length so we don't add it explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphNodes, graphEdges]);

  // ── Drag handling ────────────────────────────────────────
  // During drag, pin fx/fy so the simulation respects user intent. On
  // release, clear them and reheat so neighbors gracefully redistribute.
  const handleNodesChange = useCallback(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      onNodesChange(changes);
      const sim = simulationRef.current;
      if (!sim) return;

      for (const c of changes as Array<{ type?: string; id?: string; dragging?: boolean; position?: { x: number; y: number } }>) {
        if (c.type !== "position" || !c.id) continue;

        const d = d3NodesRef.current.find((dd) => dd.id === c.id);
        if (!d) continue;

        if (c.dragging && c.position) {
          d.fx = c.position.x;
          d.fy = c.position.y;
          d.x = c.position.x;
          d.y = c.position.y;
          // Keep sim barely warm during drag — just enough that neighbors
          // get out of the way without the whole canvas reorganizing.
          if (sim.alpha() < 0.08) sim.alpha(0.08).restart();
        } else if (c.dragging === false) {
          d.fx = null;
          d.fy = null;
          // Gentle reheat so the released node settles without dragging
          // the rest of the graph along for the ride.
          sim.alpha(0.15).restart();
        }
      }
    },
    [onNodesChange],
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const storeNode = graphNodesRef.current.find((g) => g.id === node.id);
      if (storeNode) selectNode(storeNode);
    },
    [selectNode],
  );

  const onPaneClick = useCallback(() => {
    selectNode(null);
  }, [selectNode]);

  return (
    <div ref={containerRef} className="w-full h-full relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView={false}
        minZoom={0.005}
        maxZoom={4}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
        selectNodesOnDrag={false}
        onlyRenderVisibleElements={graphNodes.length > 250}
        defaultEdgeOptions={{ type: "adEdge" }}
        proOptions={{ hideAttribution: true }}
        className="bg-transparent"
      />

      {isLoadingGraph && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/60 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm font-medium text-muted-foreground">Loading graph…</span>
          </div>
        </div>
      )}

      {!isLoadingGraph && graphNodes.length === 0 && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none">
          <div className="relative flex flex-col items-center -mt-20">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-primary/10 rounded-full blur-[60px]" />
            <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-card border border-border/50 shadow-2xl mb-6">
              <div className="absolute inset-0 rounded-full border border-primary/20 animate-[spin_4s_linear_infinite]" />
              <svg
                className="w-10 h-10 text-primary/80"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zm-7.518-.267A8.25 8.25 0 1120.25 10.5M8.288 14.212A5.25 5.25 0 1117.25 10.5" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold tracking-tight text-foreground mb-2">
              Explore Active Directory
            </h3>
            <p className="text-[15px] font-medium text-muted-foreground max-w-[320px] text-center leading-relaxed">
              Select a query from the toolbar below to visualize relationships and attack paths
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export { ADReactFlowGraphInner as ADReactFlowGraph };
